import { CubejsApi } from '@cubejs-client/core';
import { FilterType } from '@/models/filter/FilterType';
import { FILTER_CONFIG } from '@/models/filter/filterConfig';
import { FilterState, isDynamicFilter } from '@/types/filter';
import { getIsHidden } from '@/components/filter/Filter/utils';
import { sanitizeCubeQuery } from '@/utils/cube';
import { Period } from '@/types/date';
import { Channel } from '@/models/channelFocus';
import { useChannelFocusStore } from '@/pages/ChannelFocus/store/useChannelFocusStore';
import { UserInfo } from '@/types/auth';
import {
  LOCAL_FILTERS_KEY_CHANNEL_FOCUS,
  LOCAL_FILTERS_KEY_CHANNEL_FOCUS_SECONDARY,
  LOCAL_FILTERS_KEY_GLOBAL_BENCHMARKING,
} from '@/context/LocalFiltersRegistry/localFiltersKeys';
import { isRecord } from '@/utils/filter/isRecord';
import { MissingFilterEntry } from './useSharedViewValidation';

/**
 * Multilevelselect filters (trial sponsor, topic) store values as
 * SelectItem objects ({ value, label, level }) rather than plain strings.
 * This helper extracts the string identifier used for Cube comparison.
 */
const hasValue = (v: unknown): v is { value: unknown } => typeof v === 'object' && v !== null && 'value' in v;

const toStringValue = (val: unknown): string => {
  if (typeof val === 'string') return val;
  if (hasValue(val)) return String(val.value);
  return String(val);
};

/**
 * Extracts a human-readable label from a filter value for display in
 * validation messages. For SelectItem objects, returns the label;
 * for plain strings, returns the string itself.
 */
const toDisplayLabel = (val: unknown): string => {
  if (typeof val === 'string') return val;
  if (!isRecord(val)) return String(val);
  if (typeof val.label === 'string') return val.label;
  if ('value' in val) return String(val.value);
  return String(val);
};

/** Registry keys whose filter data is nested by channel (e.g. { [Channel]: { filterKey: [...] } }). */
const CHANNEL_NESTED_KEYS = new Set([
  LOCAL_FILTERS_KEY_CHANNEL_FOCUS,
  LOCAL_FILTERS_KEY_CHANNEL_FOCUS_SECONDARY,
  LOCAL_FILTERS_KEY_GLOBAL_BENCHMARKING,
]);

type ValidateFilterValuesFn = (
  filterType: FilterType,
  values: string[],
  dateRange: Period | undefined,
) => Promise<{ valid: string[]; missing: string[] }>;

/**
 * Validate a single filter's values against Cube options.
 * Returns { valid, missing } arrays. On error, all values are kept as valid.
 */
export const createValidateFilterValues = (cubejsApi: CubejsApi, userInfo: UserInfo | null): ValidateFilterValuesFn => {
  return async (filterType, values, dateRange) => {
    const config = FILTER_CONFIG[filterType];
    if (!config || !isDynamicFilter(config)) {
      return { valid: values, missing: [] };
    }

    try {
      const cubeQuery = config.getOptionsQuery({
        sourceCube: config.sourceCube,
        dateRange,
      });

      // No additional global filters are applied here intentionally.
      // The Cube backend's queryRewrite injects country-level security
      // filters based on the user's JWT, so results are already scoped
      // to the recipient's country.
      const sanitized = sanitizeCubeQuery({ ...cubeQuery, filters: cubeQuery.filters ?? [] }, true);
      const result = await cubejsApi.load(sanitized);
      const rows = result.rawData();

      let mappedOptions = rows.map(cubeRow => config.mapResult(cubeRow, { userInfo })).filter(Boolean);

      if (config.processOptions) {
        mappedOptions = config.processOptions(mappedOptions);
      }

      const availableValues = new Set(mappedOptions.map(option => option.value));
      const valid: string[] = [];
      const missing: string[] = [];

      for (const val of values) {
        if (availableValues.has(val)) {
          valid.push(val);
        } else {
          missing.push(val);
        }
      }

      return { valid, missing };
    } catch {
      return { valid: values, missing: [] };
    }
  };
};

/**
 * Validate a flat set of filter entries (e.g. { product: [...], fieldForceCode: [...] }).
 * Pushes missing entries into the shared missingFilters array and returns the valid subset.
 */
const validateFlatFilters = async (
  entries: Record<string, unknown>,
  dateRange: Period | undefined,
  missingFilters: MissingFilterEntry[],
  validateFilterValues: ValidateFilterValuesFn,
  userInfo: UserInfo | null,
): Promise<Record<string, unknown>> => {
  const validFlat: Record<string, unknown> = {};
  const promises: Promise<void>[] = [];

  for (const [filterKey, filterValue] of Object.entries(entries)) {
    if (!Array.isArray(filterValue) || filterValue.length === 0) {
      validFlat[filterKey] = filterValue;
      continue;
    }

    const filterType = filterKey as FilterType;
    const filterConfig = FILTER_CONFIG[filterType];
    if (!filterConfig || !isDynamicFilter(filterConfig)) {
      validFlat[filterKey] = filterValue;
      continue;
    }

    // Skip filters hidden for the recipient — reset to default
    if (filterConfig.hidden && userInfo && getIsHidden(filterConfig.hidden, [], { userInfo })) {
      validFlat[filterKey] = filterConfig.defaultValue;
      continue;
    }

    const stringValues = filterValue.map(toStringValue);
    const promise = validateFilterValues(filterType, stringValues, dateRange).then(({ valid, missing }) => {
      if (missing.length > 0) {
        const missingSet = new Set(missing);
        const validSet = new Set(valid);
        missingFilters.push({
          filterKey: filterType,
          filterLabel: filterConfig.label,
          missingValues: filterValue
            .filter(filterValueItem => missingSet.has(toStringValue(filterValueItem)))
            .map(toDisplayLabel),
        });
        const validOriginals = filterValue.filter(filterValueItem => validSet.has(toStringValue(filterValueItem)));
        validFlat[filterKey] = validOriginals.length > 0 ? validOriginals : filterConfig.defaultValue;
      } else {
        validFlat[filterKey] = filterValue;
      }
    });
    promises.push(promise);
  }

  await Promise.all(promises);
  return validFlat;
};

/**
 * Validate global filters — any filter with a dynamic config,
 * except COUNTRY which is handled separately via region mismatch logic.
 */
export const validateGlobalFilters = async (
  filtersToValidate: FilterState,
  validFilters: FilterState,
  dateRange: Period | undefined,
  missingFilters: MissingFilterEntry[],
  validateFilterValues: ValidateFilterValuesFn,
  userInfo: UserInfo | null,
): Promise<void> => {
  const filterTypes = Object.keys(filtersToValidate).filter((key): key is FilterType => key in FILTER_CONFIG);

  const promises = filterTypes
    .filter(filterType => {
      if (filterType === FilterType.COUNTRY) return false;
      const config = FILTER_CONFIG[filterType];
      if (!config || !isDynamicFilter(config)) return false;

      // Skip filters hidden for the recipient — reset to default
      if (config.hidden && userInfo && getIsHidden(config.hidden, [], { userInfo })) {
        validFilters[filterType] = config.defaultValue;
        return false;
      }

      const filterValue = filtersToValidate[filterType];
      return Array.isArray(filterValue) && filterValue.length > 0;
    })
    .map(async filterType => {
      const filterValue = filtersToValidate[filterType];
      if (!Array.isArray(filterValue)) return;
      const stringValues = filterValue.map(toStringValue);
      const { valid, missing } = await validateFilterValues(filterType, stringValues, dateRange);

      if (missing.length > 0) {
        const config = FILTER_CONFIG[filterType];
        const missingSet = new Set(missing);
        const validSet = new Set(valid);
        missingFilters.push({
          filterKey: filterType,
          filterLabel: config.label,
          missingValues: filterValue
            .filter(filterValueItem => missingSet.has(toStringValue(filterValueItem)))
            .map(toDisplayLabel),
        });
        const validOriginals = filterValue.filter(filterValueItem => validSet.has(toStringValue(filterValueItem)));
        if (validOriginals.length > 0) {
          Object.assign(validFilters, { [filterType]: validOriginals });
        } else {
          validFilters[filterType] = config.defaultValue;
        }
      }
    });

  await Promise.all(promises);
};

/**
 * Validate local filters (channel focus, benchmarking, insights, etc.).
 * Channels are processed sequentially because some filter configs read
 * the active channel from useChannelFocusStore to build their Cube query.
 *
 * NOTE: This function temporarily mutates useChannelFocusStore.channel as a
 * side effect. The original value is restored in the finally block. This is
 * safe because validation only runs during the shared-view loading phase
 * (behind a LoadingSpinner), so no other component observes the transient
 * channel changes.
 */
export const validateLocalFilters = async (
  localFilters: Record<string, Record<string, unknown>>,
  dateRange: Period | undefined,
  missingFilters: MissingFilterEntry[],
  validateFilterValues: ValidateFilterValuesFn,
  userInfo: UserInfo | null,
): Promise<Record<string, Record<string, unknown>>> => {
  const validLocalFilters: Record<string, Record<string, unknown>> = {};
  const originalChannel = useChannelFocusStore.getState().channel;

  try {
    for (const [registryKey, channelData] of Object.entries(localFilters)) {
      if (!CHANNEL_NESTED_KEYS.has(registryKey)) {
        validLocalFilters[registryKey] = await validateFlatFilters(
          channelData,
          dateRange,
          missingFilters,
          validateFilterValues,
          userInfo,
        );
        continue;
      }

      validLocalFilters[registryKey] = {};

      for (const [channelOrKey, channelFilters] of Object.entries(channelData)) {
        if (!isRecord(channelFilters)) {
          validLocalFilters[registryKey][channelOrKey] = channelFilters;
          continue;
        }

        const channelEnum = Object.values(Channel).find(channel => channel === channelOrKey);
        if (channelEnum) {
          useChannelFocusStore.getState().setChannel(channelEnum);
        }

        validLocalFilters[registryKey][channelOrKey] = await validateFlatFilters(
          channelFilters,
          dateRange,
          missingFilters,
          validateFilterValues,
          userInfo,
        );
      }
    }
  } finally {
    useChannelFocusStore.getState().setChannel(originalChannel);
  }

  return validLocalFilters;
};
